/**
 * Pure model for the FAKED generation delay — the client-only illusion that a
 * PREBUILT companion's PRE-GENERATED asset is being generated in real time
 * (memory `monetization-onboarding-funnel`: "fake a generation delay … even
 * though the asset already exists"). It gives the free front door the same
 * "generating…" beat as a real render, so a prebuilt gallery feels live.
 *
 * It is DELIBERATELY separate from real job status: a real `generate_image` /
 * `generate_video` job is revealed by the WS relay swapping the placeholder for
 * the finished e2 asset (PR8 F6), driven by server events — NEVER by this timer.
 * This module only models the two-phase reveal + its injected timer seam, so it
 * is unit-tested with a manual clock and no DOM (mirrors the stream reducer's
 * "inject the clock" discipline).
 */
import { createSignal } from 'solid-js';

/** The reveal's two phases: the faked "generating…" wait, then the swap. */
export type RevealPhase = 'generating' | 'revealed';

/** Default faked wait (ms) before the pre-generated asset is revealed — tunable. */
export const DEFAULT_FAKE_DELAY_MS = 2400;

/**
 * The timer seam (DIP) — `schedule` runs `fn` after `ms` and returns a cancel
 * handle. The browser adapter wraps `setTimeout`; a test injects a manual pump so
 * the reveal is driven deterministically, with no real clock and no flake.
 */
export interface RevealScheduler {
  schedule(fn: () => void, ms: number): () => void;
}

/** The default `setTimeout`-backed scheduler; its cancel clears the pending timer. */
export function browserRevealScheduler(): RevealScheduler {
  return {
    schedule(fn, ms) {
      const id = setTimeout(fn, ms);
      return () => clearTimeout(id);
    },
  };
}

/** A running faked reveal — the phase signal plus its lifecycle controls. */
export interface FakedReveal {
  /** The current phase; `generating` until {@link start} fires the scheduled swap. */
  readonly phase: () => RevealPhase;
  /** Begin the faked wait. Idempotent — a second call is a no-op. */
  start(): void;
  /** Cancel a pending reveal (component cleanup); leaves the current phase as-is. */
  cancel(): void;
}

/**
 * Build a faked reveal that starts in `generating` and flips to `revealed` once
 * `delayMs` elapses on the injected {@link RevealScheduler}. Nothing happens
 * until {@link FakedReveal.start} is called — there is no coupling to any job or
 * server signal, which is exactly what keeps the fake path distinct from the real
 * WS swap.
 */
export function createFakedReveal(
  scheduler: RevealScheduler = browserRevealScheduler(),
  delayMs: number = DEFAULT_FAKE_DELAY_MS,
): FakedReveal {
  const [phase, setPhase] = createSignal<RevealPhase>('generating');
  let cancelTimer: (() => void) | null = null;
  let started = false;

  return {
    phase,
    start() {
      if (started) {
        return;
      }
      started = true;
      cancelTimer = scheduler.schedule(() => setPhase('revealed'), delayMs);
    },
    cancel() {
      cancelTimer?.();
      cancelTimer = null;
    },
  };
}
